/**
 * Phase 132 — Super Admin Academies (/admin/livex/academies)
 * List + inline subscription controls.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  ArrowLeft, Search, Loader2, AlertTriangle, Crown, Sparkles, ChevronDown,
  Calendar, TrendingUp, Radio, Receipt, Check, Building2, Send, ExternalLink,
} from 'lucide-react';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import { LivexShimmerRow } from '@/components/livex/LivexShimmer';

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

function AcademyRow({ a, onMutate, onInvoice }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState(a.billing_cycle || 'monthly');

  const flip = async (patch) => {
    setBusy(true);
    try {
      await api.put(`/livex/admin/subscriptions/${a.id}`, patch);
      await onMutate();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const isPro = a.plan === 'pro' && a.status === 'active';

  return (
    <div className="bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden livex-card-lift" data-testid={`livex-admin-academy-${a.id}`}>
      <div className="flex items-center justify-between gap-4 p-4 sm:p-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0" style={{ background: isPro ? 'linear-gradient(135deg, #dc2626, #f97316)' : '#0c1a2e', border: isPro ? 'none' : '2px solid #22314F' }}>
            {isPro ? <Crown className="h-5 w-5 text-white" /> : <Sparkles className="h-5 w-5 text-[#FF5A1F]" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-black text-white truncate">{a.name}</h3>
              <span className={`text-[10px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 rounded ${
                isPro ? 'bg-gradient-to-r from-rose-600 to-orange-500 text-white' :
                a.plan === 'pro' && a.status === 'expired' ? 'bg-slate-700 text-white' :
                a.status === 'trialing' ? 'bg-amber-500 text-black' : 'bg-slate-700 text-white'
              }`}>{a.plan} · {a.status}</span>
              {a.billing_cycle && <span className="text-[10px] font-bold text-[#94A3B8] uppercase">{a.billing_cycle}</span>}
            </div>
            <div className="text-[11px] font-bold text-[#94A3B8] mt-0.5 truncate">
              {a.owner_email || '—'} {a.city && `· ${a.city}`} · slug: <span className="font-mono">{a.slug}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end mt-2 sm:mt-0">
          <div className="text-right">
            <div className="text-sm font-black text-white tabular-nums flex items-center gap-1"><Radio className="h-3 w-3 text-rose-500" /> {a.live_now}</div>
            <div className="text-[10px] font-bold text-[#94A3B8]">live now</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-black text-white tabular-nums flex items-center gap-1"><TrendingUp className="h-3 w-3 text-[#FF5A1F]" /> {a.total_streams}</div>
            <div className="text-[10px] font-bold text-[#94A3B8]">streams</div>
          </div>
          <button onClick={() => setOpen((v) => !v)} data-testid={`livex-admin-academy-${a.id}-toggle`}
            aria-expanded={open}
            aria-controls={`livex-admin-academy-${a.id}-panel`}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
            Manage <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {open && (
        <div id={`livex-admin-academy-${a.id}-panel`} className="p-4 sm:p-5 border-t-2 border-[#22314F] bg-[#050a14]" data-testid={`livex-admin-academy-${a.id}-panel`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="livex-label">Plan</div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => flip({ plan: 'pilot', status: 'active', billing_cycle: null })} disabled={busy}
                  data-testid={`livex-admin-academy-${a.id}-to-pilot`}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${a.plan === 'pilot' ? 'bg-[#22314F] border-[#22314F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>
                  Free Pilot
                </button>
                <button onClick={() => flip({ plan: 'pro', status: 'active', billing_cycle: cycle })} disabled={busy}
                  data-testid={`livex-admin-academy-${a.id}-to-pro`}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${a.plan === 'pro' ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>
                  Activate Pro
                </button>
                <button onClick={() => flip({ status: 'expired' })} disabled={busy || a.status === 'expired'}
                  data-testid={`livex-admin-academy-${a.id}-to-expired`}
                  className="px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 bg-[#07111F] border-rose-700 text-rose-200 hover:bg-rose-950 transition-all disabled:opacity-50">
                  Expire
                </button>
              </div>
            </div>
            <div>
              <div className="livex-label">Billing cycle</div>
              <div className="flex gap-2">
                <button onClick={() => setCycle('monthly')} className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 ${cycle === 'monthly' ? 'bg-[#22314F] border-[#22314F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>Monthly · ₹299</button>
                <button onClick={() => setCycle('yearly')} className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 ${cycle === 'yearly' ? 'bg-[#22314F] border-[#22314F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>Annual · ₹2,999</button>
              </div>
            </div>
            <div>
              <div className="livex-label">Extend trial / renewal</div>
              <div className="flex gap-2 flex-wrap">
                {[7, 14, 30, 90].map((d) => (
                  <button key={d} onClick={() => flip({ days_extension: d })} disabled={busy}
                    data-testid={`livex-admin-academy-${a.id}-extend-${d}`}
                    className="px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F] transition-all">
                    +{d}d
                  </button>
                ))}
                <span className="text-[11px] font-bold text-[#94A3B8] self-center ml-2">expires {formatDate(a.expires_at)}</span>
              </div>
            </div>
            <div>
              <div className="livex-label">Invoice</div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => onInvoice(a, 'monthly')} disabled={busy}
                  data-testid={`livex-admin-academy-${a.id}-invoice-monthly`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F] transition-all">
                  <Receipt className="h-3 w-3" /> Invoice (Monthly)
                </button>
                <button onClick={() => onInvoice(a, 'yearly')} disabled={busy}
                  data-testid={`livex-admin-academy-${a.id}-invoice-yearly`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F] transition-all">
                  <Receipt className="h-3 w-3" /> Invoice (Annual)
                </button>
              </div>
            </div>
          </div>
          {busy && <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-[#94A3B8]"><Loader2 className="h-3 w-3 animate-spin" /> Updating…</div>}
        </div>
      )}
    </div>
  );
}

export default function LivexAdminAcademies() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get('/livex/admin/academies');
      setList(data?.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    }
  };
  useEffect(() => { reload().finally(() => setLoading(false)); }, []);

  const [duplicateModal, setDuplicateModal] = useState(null);

  const createInvoice = async (academy, cycle, force = false) => {
    try {
      const url = force ? '/livex/admin/invoices?force=true' : '/livex/admin/invoices';
      const { data } = await api.post(url, { academy_id: academy.id, plan: 'pro', billing_cycle: cycle });
      setCreatedInvoice(data);
      setDuplicateModal(null);
      setTimeout(() => setCreatedInvoice(null), 5000);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Phase 138A — handle the 409 duplicate-invoice path with a modal
      if (status === 409 && detail && typeof detail === 'object' &&
          detail.error === 'duplicate_invoice_for_period') {
        setDuplicateModal({
          academy,
          cycle,
          existing: detail.existing_invoice,
          message: detail.message,
          period_label: detail.period_label,
        });
        return;
      }
      alert(typeof detail === 'string' ? detail : (detail?.message || 'Failed to create invoice'));
    }
  };

  const reissueExisting = async () => {
    if (!duplicateModal?.existing?.id) return;
    try {
      await api.post(`/livex/admin/invoices/${duplicateModal.existing.id}/reissue`);
      setCreatedInvoice({ ...duplicateModal.existing, reissued: true });
      setDuplicateModal(null);
      setTimeout(() => setCreatedInvoice(null), 5000);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reissue failed');
    }
  };

  const filtered = list.filter((a) => {
    if (q && !`${a.name} ${a.slug} ${a.owner_email}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === 'pro' && !(a.plan === 'pro' && a.status === 'active')) return false;
    if (filter === 'pilot' && a.plan !== 'pilot') return false;
    if (filter === 'live' && !a.live_now) return false;
    return true;
  });

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-admin-academies">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/admin/livex')} className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Overview
          </button>
          <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · ACADEMIES</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl sm:text-3xl font-black text-white">Academies on StreamX</h1>
          <div className="text-xs font-bold text-[#94A3B8]" data-testid="livex-admin-academies-count">{filtered.length} of {list.length}</div>
        </div>

        {/* Search + filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, slug, owner email..."
              data-testid="livex-admin-academies-search"
              className="livex-input pl-10"
            />
          </div>
          {[['all', 'All'], ['pro', 'Pro'], ['pilot', 'Pilot'], ['live', 'Live now']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`livex-admin-academies-filter-${k}`}
              className={`px-3 py-2.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                filter === k ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
              }`}>
              {l}
            </button>
          ))}
        </div>

        {createdInvoice && (
          <div className="p-3 bg-emerald-950 border-2 border-emerald-700 rounded-lg text-emerald-100 text-sm font-bold flex items-center gap-2" data-testid="livex-admin-invoice-created">
            <Check className="h-4 w-4 text-emerald-400" /> Invoice <span className="font-mono">{createdInvoice.invoice_number}</span> generated — total ₹{(createdInvoice.total_inr_paise / 100).toFixed(2)}
            <Link to="/admin/livex/invoices" className="ml-2 underline">view all</Link>
          </div>
        )}
        {error && (
          <div className="p-3 bg-rose-950 border-2 border-rose-700 rounded-lg text-rose-100 text-sm font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3" data-testid="livex-admin-academies-loading">
            {Array.from({ length: 5 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <LivexEmptyState
            testid="livex-admin-academies-empty"
            icon={Building2}
            title={q || filter !== 'all' ? 'No academies match this filter' : 'No academies on StreamX yet'}
            body={q || filter !== 'all'
              ? 'Clear the search or pick a different filter to see all academies.'
              : 'Once academies sign up to Out-Play, they\'ll appear here for subscription provisioning.'}
            secondaryLabel="Back to overview"
            secondaryTo="/admin/livex"
            compact
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => <AcademyRow key={a.id} a={a} onMutate={reload} onInvoice={createInvoice} />)}
          </div>
        )}
      </div>

      {/* Phase 138A — Duplicate invoice modal */}
      {duplicateModal && (
        <div
          data-testid="livex-admin-duplicate-invoice-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDuplicateModal(null); }}
        >
          <div className="w-full max-w-md p-6 bg-[#07111F] border-2 border-[#FF5A1F] rounded-2xl text-white"
            style={{ boxShadow: '0 24px 64px rgba(255,90,31,0.30)' }}>
            <div className="flex items-start gap-3 mb-4">
              <div className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#dc2626,#f97316)' }}>
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-[11px] font-black tracking-[0.18em] uppercase text-[#FF5A1F]">Invoice already exists</div>
                <h2 className="text-xl font-black text-white mt-1">{duplicateModal.period_label}</h2>
              </div>
            </div>
            <p className="text-sm text-[#cbd5e1] leading-relaxed mb-4">{duplicateModal.message}</p>
            <div className="p-3 bg-[#0c1a2e] border-2 border-[#22314F] rounded-lg mb-5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">Existing invoice</div>
                  <div className="text-sm font-black text-white font-mono">{duplicateModal.existing?.invoice_number}</div>
                  <div className="text-[11px] font-bold text-[#94A3B8] mt-0.5 uppercase">
                    {duplicateModal.existing?.status} · ₹{((duplicateModal.existing?.total_inr_paise || 0) / 100).toFixed(2)}
                  </div>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/livex/admin/invoices/${duplicateModal.existing?.id}/html`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="livex-admin-duplicate-invoice-view"
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-white border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> View
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={reissueExisting}
                data-testid="livex-admin-duplicate-invoice-reissue"
                className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white rounded-lg border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
              >
                <Send className="h-3.5 w-3.5" /> Reissue & re-send
              </button>
              <button
                onClick={() => setDuplicateModal(null)}
                data-testid="livex-admin-duplicate-invoice-cancel"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#94A3B8] rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
